import { createAsyncThunk } from "@reduxjs/toolkit";
import request from "../../../../service";
import { IErrorResponse } from "../../../../types";
import {
    deletedRestoreStudents,
    deleteStudents,
    setStudents,
    setStudentsEducationalDetails,
    setStudentsRestore,
    setStudentsTrainingsSkills,
    setStudentsWorkplaceDetails,
} from "./studentsSlice";

const GetStudents = createAsyncThunk("get/students", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await request.get("/students");
        dispatch(setStudents(response.data));
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
    }
});

const GetStudentsWorkplaceDetails = createAsyncThunk(
    "get/students/workplace/details",
    async (id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.get(`/student_workplaces/${id}`);
            dispatch(setStudentsWorkplaceDetails(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
        }
    }
);

const GetStudentsTrainingsSkills = createAsyncThunk(
    "get/students/trainings/skills",
    async (id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.get(`/student-trainings/${id}`);
            dispatch(setStudentsTrainingsSkills(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
        }
    }
);

const CreateStudentsTrainingsSkills = createAsyncThunk(
    "create/students/trainings/skills",
    async (payload: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.post("/student-trainings", payload);
            dispatch(setStudentsTrainingsSkills(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditStudentsTrainingsSkills = createAsyncThunk(
    "edit/students/trainings/skills",
    async (data: any, { rejectWithValue, dispatch }) => {
        const { formData, id } = data;
        try {
            const response = await request.post(`/student-trainings/${id}`, formData);
            dispatch(setStudentsTrainingsSkills(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetStudentsEducationalDetails = createAsyncThunk(
    "get/students/educational/details",
    async (id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.get(`/student-educational-details/${id}`);
            dispatch(setStudentsEducationalDetails(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
        }
    }
);

const CreateStudents = createAsyncThunk("create/students", async (payload: any, { rejectWithValue }) => {
    try {
        const response = await request.post("/students", payload);

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const CreateStudentsWorkplaceDetails = createAsyncThunk(
    "create/students/workplace/details",
    async (payload: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.post("/student_workplaces", payload);
            dispatch(setStudentsWorkplaceDetails(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditStudentsWorkplaceDetails = createAsyncThunk(
    "edit/students/workplace/details",
    async (data: any, { rejectWithValue, dispatch }) => {
        const { formData, id } = data;
        try {
            const response = await request.post(`/student_workplaces/${id}`, formData);
            dispatch(setStudentsWorkplaceDetails(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditStudents = createAsyncThunk("edit/students", async (data: any, { rejectWithValue }) => {
    const { formData, id } = data;
    try {
        const response = await request.post(`/students/${id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
    }
});

const DeleteStudents = createAsyncThunk("delete/students/", async (id: number, thunkAPI) => {
    try {
        const response = await request.delete(`/students/${id}`);
        thunkAPI.dispatch(deleteStudents(id));
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return thunkAPI.rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const GetDeleteStudents = createAsyncThunk("get/delete/students", async (_, { dispatch, rejectWithValue }) => {
    try {
        const response = await request.get(`/students/deleted-items`);

        dispatch(setStudentsRestore(response.data));

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const RestoreDeleteStudents = createAsyncThunk(
    "restore/delete/students",
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            const response = await request.post(`/students/restore/${id}`);

            dispatch(deletedRestoreStudents(id));

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const StudentsFamily = createAsyncThunk("students/family", async (id: number, { rejectWithValue }) => {
    try {
        const response = await request.get(`/students-family-details/${id}`);

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

export {
    CreateStudents,
    GetStudents,
    DeleteStudents,
    EditStudents,
    RestoreDeleteStudents,
    GetDeleteStudents,
    GetStudentsWorkplaceDetails,
    CreateStudentsWorkplaceDetails,
    GetStudentsTrainingsSkills,
    GetStudentsEducationalDetails,
    EditStudentsWorkplaceDetails,
    CreateStudentsTrainingsSkills,
    EditStudentsTrainingsSkills,
    StudentsFamily,
};
